import { FormErrorText } from '@exam-management/components';
import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigStatus, QConfigStructureCategory } from '@exam-management/graphql';
import { QConfigPublishValidationErrorCode } from '@exam-management/qconfig-detail';
import { AddIcon, Box, Button, Flex, Text, Tooltip } from '@shared/components';
import { dayjs } from '@shared/utils';
import uniqueId from 'lodash/uniqueId';
import { useEffect, useMemo } from 'react';

import { ExamTimingByStructureCard } from '../../components';
import { type QConfigSettingsFormInput, useQConfigSettingsFormContext } from '../../providers';

interface ExamTimingByStructuresProps {
  disabled?: boolean;
}

export default function ExamTimingByStructures(props: Readonly<ExamTimingByStructuresProps>) {
  const { disabled: isDisabled } = props;

  const { qConfigData } = useQConfig();
  const { structures: qConfigStructures } = qConfigData;

  const formContext = useQConfigSettingsFormContext();
  const { watch, setValue, trigger, formState } = formContext;

  // NOTE: tricky approach, because watch can't detect changes of value inside each elements.
  const formStructures = [...watch('examTiming.structures')];

  const qConfigStructureMap = useMemo(() => {
    const map = new Map<string, (typeof qConfigStructures)[number]>();
    qConfigStructures.forEach((structure) => {
      map.set(structure.id, structure);
    });
    return map;
  }, [qConfigStructures]);

  const structures = [QConfigStatus.Draft].includes(qConfigData.status)
    ? formStructures
    : (qConfigData.timeLimit?.values.map((value) => ({
        id: uniqueId('structure-'),
        parts:
          value.qConfigStructureIds?.map((structureId) => ({
            label: qConfigStructureMap.get(structureId)?.name ?? '',
            value: structureId,
          })) ?? [],
        timeLimit: dayjs().startOf('day').add(value.value, 'second').toDate(),
      })) ?? []);

  const partOptions = useMemo(() => {
    return qConfigStructures
      .filter((structure) => structure.category === QConfigStructureCategory.Part)
      .sort((a, b) => {
        return (a.order ?? 0) - (b.order ?? 0);
      })
      .map((structure) => ({ label: structure.name ?? '', value: structure.id }));
  }, [qConfigStructures]);

  const selectedParts = useMemo(() => {
    return structures.flatMap((structure) => structure.parts.map((part) => part.value));
  }, [structures]);

  const shouldDisabledAddCard = useMemo(() => {
    return (
      partOptions.length === selectedParts.length ||
      structures.some((structure) => !structure.parts.length)
    );
  }, [structures, selectedParts, partOptions]);

  const shouldShowTooltip = useMemo(() => {
    return partOptions.length === selectedParts.length;
  }, [partOptions, selectedParts]);

  const errorType = formState.errors.examTiming?.structures?.root?.types?.validate;
  const errorMessage = formState.errors.examTiming?.structures?.root?.message;

  useEffect(() => {
    if (!structures.length) {
      setValue('examTiming.structures', [
        {
          id: uniqueId('structure-'),
          parts: [],
          timeLimit: null,
        },
      ]);
    }
  }, []);

  useEffect(() => {
    const { unsubscribe } = watch((_, info) => {
      const { name, type, values } = info;
      if (type === 'change' && name?.includes('timeLimit')) {
        trigger('examTiming.structures');
      }
      if (type === 'change' && name?.includes('parts')) {
        const totalSelectedParts =
          (values as QConfigSettingsFormInput).examTiming?.structures.reduce(
            (total, structure) => total + structure.parts.length,
            0,
          ) ?? 0;
        if (totalSelectedParts === partOptions.length) {
          formContext.clearErrors('examTiming.structures');
        }
      }
    });
    return () => unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (
      errorType === QConfigPublishValidationErrorCode.Q_CONFIG_STRUCTURES_ON_TIME_LIMIT_NOT_COMPLETE
    ) {
      for (let i = 0; i < structures.length; i++) {
        formContext.setError(`examTiming.structures.${i}.parts`, { message: '' });
      }
    }
  }, [errorType]);

  const handleDiscardCard = (order: number) => {
    const newStructures = structures.filter((_, index) => index !== order);
    setValue('examTiming.structures', newStructures, {
      shouldDirty: true,
    });
  };

  const handleAddCard = () => {
    setValue(
      'examTiming.structures',
      [
        ...structures,
        {
          id: uniqueId('structure-'),
          parts: [],
          timeLimit: null,
        },
      ],
      {
        shouldDirty: true,
      },
    );
  };

  return (
    <Flex sx={{ width: '100%', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
      {structures.map((structure, order) => {
        const { id, parts: currentParts } = structure;
        const availableOptions = partOptions.filter(
          (option) =>
            !selectedParts.includes(option.value) ||
            currentParts.flatMap((currentPart) => currentPart.value).includes(option.value),
        );
        return (
          <ExamTimingByStructureCard
            key={id}
            title={`ช่วงเวลาที่ ${order + 1}`}
            order={order}
            selection={{
              name: `examTiming.structures.${order}.parts`,
              label: 'เลือกหมวดที่ต้องการ',
              placeholder: !currentParts.length ? 'เลือกหมวดที่ต้องการ' : '',
              options: availableOptions,
              disabled: isDisabled,
            }}
            timeInput={{
              name: `examTiming.structures.${order}.timeLimit`,
              label: 'กำหนดระยะเวลาสอบ (HH:MM)',
              disabled: isDisabled,
            }}
            onDiscard={
              structures.length > 1 && !isDisabled ? () => handleDiscardCard(order) : undefined
            }
          />
        );
      })}
      {errorMessage ? <FormErrorText>{errorMessage}</FormErrorText> : null}
      {!isDisabled ? (
        <Tooltip
          title={
            <Text component='span' variant='caption'>
              ไม่สามารถเพิ่มช่วงเวลาสอบได้
              <br />
              เนื่องจากหมวดทั้งหมดได้ถูกเพิ่มแล้ว
            </Text>
          }
          arrow
          placement='left'
          disableHoverListener={!shouldShowTooltip}
        >
          {/* NOTE: Tooltip does not work on disabled element https://mui.com/material-ui/react-tooltip/#disabled-elements */}
          <Box component='span' sx={{ maxWidth: 'fit-content' }}>
            <Button
              variant='outlined'
              size='large'
              color='secondary'
              startIcon={<AddIcon />}
              onClick={handleAddCard}
              disabled={shouldDisabledAddCard}
            >
              เพิ่มช่วงเวลาสอบ
            </Button>
          </Box>
        </Tooltip>
      ) : null}
    </Flex>
  );
}
